import { useCallback, useState } from "react";
import { FiUploadCloud } from "react-icons/fi";

interface FileDropzoneProps {
  /** Currently selected files */
  files: File[];
  /** Callback when files change */
  onFilesChange: (files: File[]) => void;
  /** Accepted MIME types (e.g., "audio/*", "image/*") */
  accept: string;
  /** Maximum number of files allowed */
  maxFiles?: number;
  /** Maximum file size in bytes */
  maxSize?: number;
  /** Label for the dropzone */
  label: string;
  /** Whether the field is required */
  required?: boolean;
  /** Custom validation function */
  validateFile?: (file: File) => { valid: boolean; error?: string };
}

/**
 * FileDropzone - Drag-and-drop file upload zone
 *
 * Supports multi-file selection, validation, and visual feedback.
 */
export function FileDropzone({
  files,
  onFilesChange,
  accept,
  maxFiles = 10,
  maxSize,
  label,
  required = false,
  validateFile,
}: FileDropzoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = useCallback(
    (newFiles: FileList | File[]) => {
      setError(null);
      const fileArray = Array.from(newFiles);

      // Check max files
      if (files.length + fileArray.length > maxFiles) {
        setError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      // Validate each file
      const validFiles: File[] = [];
      for (const file of fileArray) {
        // Check file size
        if (maxSize && file.size > maxSize) {
          setError(`File "${file.name}" exceeds size limit`);
          continue;
        }

        // Custom validation
        if (validateFile) {
          const result = validateFile(file);
          if (!result.valid) {
            setError(result.error || `Invalid file: ${file.name}`);
            continue;
          }
        }

        validFiles.push(file);
      }

      if (validFiles.length > 0) {
        onFilesChange([...files, ...validFiles]);
      }
    },
    [files, maxFiles, maxSize, validateFile, onFilesChange]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
      }
      // Reset input so same file can be selected again
      e.target.value = "";
    },
    [handleFiles]
  );

  return (
    <div className="mb-6">
      {/* Label */}
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {/* Dropzone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          relative flex min-h-[120px] cursor-pointer flex-col items-center justify-center
          rounded-lg border-2 border-dashed p-6 transition-colors
          ${
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
          }
        `}
      >
        <input
          type="file"
          accept={accept}
          multiple={maxFiles > 1}
          onChange={handleInputChange}
          className="absolute inset-0 cursor-pointer opacity-0"
          aria-label={`Upload ${label}`}
        />

        <FiUploadCloud
          className={`mb-2 h-8 w-8 ${isDragging ? "text-blue-500" : "text-gray-400"}`}
        />

        <p className="text-sm text-gray-600">
          Drag files here to upload or{" "}
          <span className="font-medium text-blue-600">click to browse</span>
        </p>

        {maxFiles > 1 && (
          <p className="mt-1 text-xs text-gray-500">Up to {maxFiles} files allowed</p>
        )}
      </div>

      {/* Error message */}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      {/* File count indicator */}
      {files.length > 0 && (
        <p className="mt-2 text-sm text-gray-600">
          {files.length} file{files.length !== 1 ? "s" : ""} selected
        </p>
      )}
    </div>
  );
}

export default FileDropzone;
